'use client';

/**
 * 全站只挂一处：PayPal / Stripe Hosted Checkout 整页跳转回跳后，统一从 URL 上的
 * option/order_id 等参数完成 commerce confirm。与既有 FirebaseLoginRedirectHandler 对齐。
 */

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import { onActivityPaymentSuccess } from '@/activity-orchestration/on-payment-success';
import { runPaywallOfferAfterPaymentAbandon } from '@/activity-orchestration/scenes/paywall';
import { CommerceApi } from '@/apis/commerce';
import LoadingAnimation from '@/components/loading-animation';
import { useAppRouter } from '@/hooks/use-app-router';
import { assertConfirmOrderResponse, confirmCommerceOrderWithPolling } from '@/hooks/use-pay/confirm-with-polling';
import { finalizePaymentSuccess } from '@/hooks/use-pay/finalize-payment-success';
import { clearPersistedPayPendingMeta, readPersistedPayPendingMeta } from '@/hooks/use-pay/paypal-checkout-redirect';
import {
    EHostedCheckoutReturnQueryKey,
    parseHostedCheckoutReturnFromSearchParams,
    stripHostedCheckoutReturnFromUrl,
} from '@/lib/pay/hosted-checkout-return-params';
import {
    clearPayPendingReturnUrls,
    persistPaySuccessReturnChapter,
    isBackForwardNavigation,
    isSafeInternalPath,
    mergeLandingPersistAfterCheckoutReturn,
    readPayPendingReturnUrls,
    tryRestoreStrippedPayReturnUrl,
} from '@/lib/pay/hosted-checkout-return-restore';
import { useToast } from '@/providers/root/toast';
import { useAssetStore } from '@/store/asset';
import { readArmsFailDetail } from '@/utils/arms/paypal-events';
import {
    queuePayResultTrackingAfterRefresh,
    trackRechargePageResultPayCancel,
    trackRechargePageResultPayFail,
} from '@/utils/tracker/pay';

import {
    formatConfirmOrderFailDetail,
    mapConfirmStatusToCheckoutFailReason,
    metaToPayExtras,
    metaToStaticContext,
    reportCheckoutReturnFail,
    reportCheckoutReturnSuccess,
} from './checkout-return-arms';

/**
 * 支付取消 / 失败：先拉挽留；有可见弹窗（当场挽留或已有占位）时不出 toast。
 * @param orderId 用于同单防重
 * @param toastMessage 无弹窗时的提示文案
 * @param addToast toast 入口
 */
async function notifyPaymentAbandon(
    orderId: string | undefined,
    toastMessage: string,
    addToast: (message: string) => void
): Promise<void> {
    try {
        const { suppressToast } = await runPaywallOfferAfterPaymentAbandon(orderId);
        if (!suppressToast) {
            addToast(toastMessage);
        }
    } catch (error) {
        console.error('[HostedCheckoutReturn] paywall retention offer failed', error);
        addToast(toastMessage);
    }
}

// const PayCancelReasonModal = dynamic(() => import('@/components/paypal-cancel-reason-modal'), {
//     ssr: false,
//     loading: () => null,
// });

let hasProcessedReturn = false;

const CONSUMED_KEY = (orderId: string): string => `hosted_checkout_consumed_${orderId}`;

/** 兼容旧版仅写入 paypal_consumed_* 的 session */
const LEGACY_CONSUMED_KEY = (orderId: string): string => `paypal_consumed_${orderId}`;

const DEFAULT_STORE_PAYPAL = 'PAYPAL';
const DEFAULT_STORE_STRIPE_CHECKOUT = 'STRIPE_CHECKOUT';

function getReturnSearchParams(): URLSearchParams {
    if (typeof window === 'undefined') {
        return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
}

/**
 * 服务端回跳 URL 已携带正确章节时，即使本地支付元数据缺失，也要保留该恢复目标。
 */
function readReaderChapterIdFromCurrentUrl(): string {
    if (typeof window === 'undefined' || window.location.pathname !== '/reader') {
        return '';
    }
    return new URLSearchParams(window.location.search).get('chapter_id')?.trim() ?? '';
}

function resolveConfirmStore(meta: ReturnType<typeof readPersistedPayPendingMeta>): string {
    if (meta?.store) {
        return meta.store;
    }
    if (['card', 'apple', 'google'].includes(meta?.payment_method ?? '')) {
        return DEFAULT_STORE_STRIPE_CHECKOUT;
    }
    return DEFAULT_STORE_PAYPAL;
}

function isOrderReturnConsumed(orderId: string): boolean {
    try {
        return (
            window.sessionStorage.getItem(CONSUMED_KEY(orderId)) === '1' ||
            window.sessionStorage.getItem(LEGACY_CONSUMED_KEY(orderId)) === '1'
        );
    } catch {
        return false;
    }
}

function markOrderReturnConsumed(orderId: string): void {
    try {
        window.sessionStorage.setItem(CONSUMED_KEY(orderId), '1');
    } catch {
        // sessionStorage 不可用时跳过防重保护
    }
}

export default function HostedCheckoutReturnConfirmHandler() {
    const t = useTranslations('Paywall');
    const router = useAppRouter();

    const { addToast } = useToast();
    const [isConfirming, setIsConfirming] = useState(false);
    // const [showCancelModal, setShowCancelModal] = useState(false);
    // const [orderId, setOrderId] = useState('');
    // const handleCancelModalClose = useCallback(() => setShowCancelModal(false), []);
    const paymentCancelSurveyOrderId = useAssetStore(state => state.paymentCancelSurveyOrderId);

    const returnToOrigin = (originReturnPath?: string, fallbackChapterId = '') => {
        if (!originReturnPath || !isSafeInternalPath(originReturnPath)) {
            return;
        }

        // 支付前 URL 是用户实际阅读位置的快照；成功和取消均以它为准。
        // 仅当快照本身缺少 chapter_id 时，才用订单创建时保存的目标章节兜底。
        try {
            const originUrl = new URL(originReturnPath, window.location.origin);
            if (!originUrl.searchParams.get('chapter_id') && fallbackChapterId) {
                originUrl.searchParams.set('chapter_id', fallbackChapterId);
            }
            router.replace(`${originUrl.pathname}${originUrl.search}${originUrl.hash}`);
        } catch {
            router.replace(originReturnPath);
        }
    };

    useEffect(() => {
        if (!paymentCancelSurveyOrderId) {
            return;
        }
        // setOrderId(paymentCancelSurveyOrderId);
        // setShowCancelModal(true);
        useAssetStore.getState().clearPaymentCancelSurvey();
    }, [paymentCancelSurveyOrderId]);

    useEffect(() => {
        if (hasProcessedReturn) {
            return;
        }

        if (tryRestoreStrippedPayReturnUrl()) {
            return;
        }

        const returnSearchParams = getReturnSearchParams();
        const ret = parseHostedCheckoutReturnFromSearchParams(returnSearchParams);
        if (!ret) {
            const pending = isBackForwardNavigation() ? readPayPendingReturnUrls() : null;
            if (pending) {
                hasProcessedReturn = true;
                const meta = readPersistedPayPendingMeta(pending.orderId);
                reportCheckoutReturnFail(pending.orderId, meta, '', 'paypal_return_cancel');
                // setOrderId(pending.orderId);
                if (meta) {
                    trackRechargePageResultPayCancel({
                        result_status: 'slip_cancel',
                        staticContext: metaToStaticContext(meta),
                        payExtras: metaToPayExtras(meta),
                        payment_method: meta.payment_method,
                        order_id: pending.orderId,
                    });
                }
                // setShowCancelModal(true);
                notifyPaymentAbandon(pending.orderId, t('pay_payment_canceled'), addToast)
                    .finally(() => {
                        if (pending.orderId) {
                            clearPersistedPayPendingMeta(pending.orderId);
                        }
                        clearPayPendingReturnUrls();
                        returnToOrigin(pending.originReturnPath);
                    })
                    .catch(() => {});
            }
            return;
        }
        const returnScene = returnSearchParams.get(EHostedCheckoutReturnQueryKey.ReturnScene)?.trim() ?? '';
        const pendingReturn = readPayPendingReturnUrls();
        // cancel 回跳在少数网关场景可能不带 order_id；此时 session 中仅有当前待支付订单，可安全兜底。
        const originReturnPath =
            pendingReturn && (!ret.orderId || pendingReturn.orderId === ret.orderId)
                ? pendingReturn.originReturnPath
                : undefined;
        hasProcessedReturn = true;

        try {
            // 去掉网关回跳参数后，用跳转前快照把落地五参补回地址栏
            const cleanUrl = mergeLandingPersistAfterCheckoutReturn(
                stripHostedCheckoutReturnFromUrl(window.location.href)
            );
            if (cleanUrl !== window.location.pathname + window.location.search + window.location.hash) {
                window.history.replaceState(window.history.state, '', cleanUrl);
            }
        } catch {
            // history.replaceState 在极端环境可能抛错，sessionStorage 防重仍可兜底
        }

        const meta = readPersistedPayPendingMeta(ret.orderId);
        const store = resolveConfirmStore(meta);
        // setOrderId(ret.orderId);

        if (ret.status === 'cancel') {
            reportCheckoutReturnFail(ret.orderId, meta, returnScene, 'paypal_return_cancel');
            if (meta) {
                trackRechargePageResultPayCancel({
                    result_status: 'click_cancel',
                    staticContext: metaToStaticContext(meta),
                    payExtras: metaToPayExtras(meta),
                    payment_method: meta.payment_method,
                    order_id: ret.orderId,
                });
            }
            // setShowCancelModal(true);
            notifyPaymentAbandon(ret.orderId, t('pay_payment_canceled'), addToast)
                .finally(() => {
                    if (ret.orderId) {
                        clearPersistedPayPendingMeta(ret.orderId);
                    }
                    clearPayPendingReturnUrls();
                    returnToOrigin(originReturnPath);
                })
                .catch(() => {});
            return;
        }

        if (isOrderReturnConsumed(ret.orderId)) {
            clearPersistedPayPendingMeta(ret.orderId);
            clearPayPendingReturnUrls();
            returnToOrigin(originReturnPath);
            return;
        }

        setIsConfirming(true);
        (async () => {
            let successToastMessage = '';
            try {
                const data = await confirmCommerceOrderWithPolling(
                    async args => {
                        const res = await CommerceApi.postOrderConfirmV1({
                            order_id: args.commerceOrderId,
                            client_result: {
                                status: ret.status,
                                store,
                                store_order_id: args.paypalOrderId,
                            },
                            ts: String(Date.now()),
                        });
                        return assertConfirmOrderResponse(res);
                    },
                    { commerceOrderId: ret.orderId, paypalOrderId: ret.storeOrderId }
                );

                const staticContext = meta
                    ? metaToStaticContext(meta)
                    : {
                          trigger_scene: '',
                          gear_id: '',
                          is_balance_enough: 0,
                          paywall_template_id: '',
                          version: 0,
                          checksum: '',
                          book_id: '',
                          chapter_id: '',
                          chapter_price: '',
                          is_auto_unlock_on: '',
                          paywall_style: '',
                      };
                const payExtras = meta ? metaToPayExtras(meta) : { money: 0, goods_type: 'recharge' as const };
                const paymentMethod = meta?.payment_method ?? '';

                if (data.status === 'PAID') {
                    // pending return 会在 finally 清理；成功章节单独保留到 ReaderCore 完成正文定位。
                    const successReturnChapterId = meta?.chapter_id || readReaderChapterIdFromCurrentUrl();
                    persistPaySuccessReturnChapter(successReturnChapterId);
                    reportCheckoutReturnSuccess(ret.orderId, meta, returnScene);
                    queuePayResultTrackingAfterRefresh({
                        staticContext,
                        payExtras,
                        payment_method: paymentMethod,
                        order_id: ret.orderId,
                    });
                    // 统一收口单独 try/catch：finalize 失败不走「确认失败」+ 挽留
                    try {
                        const assetRefreshed = await finalizePaymentSuccess({
                            activityId: meta?.activity_id,
                            toastMessage: t('pay_store_success'),
                            assetRefreshFailToast: t('pay_balance_refresh_failed'),
                            notifyScene: meta?.notify_scene ?? true,
                            showToast: false,
                        });
                        successToastMessage = assetRefreshed ? t('pay_store_success') : t('pay_balance_refresh_failed');
                    } catch (finalizeErr) {
                        console.error('[HostedCheckoutReturn] finalize failed', finalizeErr);
                        onActivityPaymentSuccess(meta?.activity_id);
                    }
                } else {
                    reportCheckoutReturnFail(
                        ret.orderId,
                        meta,
                        returnScene,
                        mapConfirmStatusToCheckoutFailReason(data.status, meta),
                        formatConfirmOrderFailDetail(data)
                    );
                    trackRechargePageResultPayFail({
                        staticContext,
                        payExtras,
                        payment_method: paymentMethod,
                        order_id: ret.orderId,
                    });
                    await notifyPaymentAbandon(ret.orderId, 'Payment not confirmed', addToast);
                }
            } catch (err) {
                reportCheckoutReturnFail(
                    ret.orderId,
                    meta,
                    returnScene,
                    'order_confirm_api_failed',
                    readArmsFailDetail(err)
                );
                if (meta) {
                    trackRechargePageResultPayFail({
                        staticContext: metaToStaticContext(meta),
                        payExtras: metaToPayExtras(meta),
                        payment_method: meta.payment_method,
                        order_id: ret.orderId,
                    });
                }
                await notifyPaymentAbandon(ret.orderId, 'Order confirmation failed', addToast);
            } finally {
                // 仅在确认流程收口后标记，刷新中断时可安全重新确认，而不会卡在回跳页。
                markOrderReturnConsumed(ret.orderId);
                clearPersistedPayPendingMeta(ret.orderId);
                clearPayPendingReturnUrls();
                setIsConfirming(false);
                // 无论成功、失败或取消，均回到发起 Hosted Checkout 前所在的业务页面。
                returnToOrigin(originReturnPath, meta?.chapter_id ?? '');
                // 让 loading 先卸载，再显示结果提示，避免 Toast 被遮罩短暂盖住。
                if (successToastMessage) {
                    window.setTimeout(() => addToast(successToastMessage), 0);
                }
            }
        })().catch(() => {
            setIsConfirming(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 只处理一次回跳
    }, []);

    return (
        <>
            <LoadingAnimation
                mask
                textClassName='!text-[12px]'
                cardClassName='w-[165px]'
                visible={isConfirming}
                text={t('pay_paying')}
            />
            {/* <PayCancelReasonModal visible={showCancelModal} onClose={handleCancelModalClose} orderId={orderId} /> */}
        </>
    );
}
